import type { App } from './App';
import type { GameConfig, GameResult } from '../types';
import { GameEngine, type GamePhase } from '../core/GameEngine';
import { ResultModal } from './ResultModal';
import { resumeAudioCtx } from '../audio/ctx';

export class GameScreen {
  private el: HTMLElement;
  private app: App;
  private config: GameConfig;
  private engine: GameEngine | null = null;
  private canvas!: HTMLCanvasElement;
  private rankList!: HTMLElement;
  private result: GameResult | null = null;
  private modal: ResultModal | null = null;
  private rankItems: Array<{ rank: number; name: string; color: string }> = [];
  private unsubscribeOnline: (() => void) | null = null;
  private onlineResultApplied = false;
  private onlineResultBroadcasted = false;
  private resultReady = false;
  private mediaRecorder: MediaRecorder | null = null;
  private recordingChunks: Blob[] = [];
  private replayUrl: string | null = null;
  private replayOverlay: HTMLElement | null = null;
  private guideOverlay: HTMLElement | null = null;
  private liveRankTimer: ReturnType<typeof setInterval> | null = null;

  constructor(container: HTMLElement, app: App, config: GameConfig) {
    this.app = app;
    this.config = config;

    this.el = document.createElement('div');
    this.el.className = 'game-screen';
    container.appendChild(this.el);
    this.render();
  }

  private render() {
    const modeLabel: Record<string, string> = {
      single: '한 명 뽑기',
      multi: `${this.config.pickCount}명 뽑기`,
      team: `${this.config.teamCount}팀 나누기`,
      reward: '보상 룰렛',
    };

    this.el.innerHTML = `
      <div class="game-topbar">
        <button class="btn-icon-sm" id="btn-game-back" title="나가기" aria-label="나가기">✕</button>
        <span class="game-mode-label">${modeLabel[this.config.mode]}</span>
        <span class="game-student-count">${this.config.students.length}명</span>
        <button class="btn-result-ready disabled" id="btn-show-result" title="결과 보기">결과 보기</button>
        <button class="recording-pill" id="recording-pill" title="누르면 녹화를 멈춰요">REC</button>
        <button class="btn-icon-sm disabled" id="btn-replay-game" title="리플레이" aria-label="리플레이">▶</button>
        <button class="btn-icon-sm disabled" id="btn-download-replay" title="녹화 다운로드" aria-label="녹화 다운로드">⬇</button>
        <button class="btn-icon-sm" id="btn-fullscreen-game" title="전체화면" aria-label="전체화면">⛶</button>
      </div>

      <div class="game-layout">
        <div class="canvas-wrapper" id="canvas-wrapper">
          <canvas id="game-canvas"></canvas>
        </div>
        <div class="game-sidebar">
          <div class="sidebar-title">실시간 순위</div>
          <div class="rank-list" id="rank-list"></div>
          <div class="sidebar-students">
            <div class="sidebar-subtitle">참가 학생</div>
            <div class="student-badges">
              ${this.config.students.map((s, i) => `
                <span class="student-badge" style="--hue:${(360/this.config.students.length)*i}" id="sb-${s.id}">${s.name}</span>
              `).join('')}
            </div>
          </div>
        </div>
      </div>
    `;

    this.canvas = this.el.querySelector<HTMLCanvasElement>('#game-canvas')!;
    this.rankList = this.el.querySelector<HTMLElement>('#rank-list')!;

    this.initEngine();
    this.bindOnlineResultSync();
    this.bindEvents();
    this.showGuide();
  }

  private initEngine(): GameEngine {
    const engine = new GameEngine(this.canvas, this.config);
    this.engine = engine;

    engine.callbacks.onGoal = (rank, name, color) => {
      this.rankItems.push({ rank, name, color });
      this.markStudentFinished(name);
    };

    engine.callbacks.onResultReady = (result) => {
      this.setResultReady(result);
    };

    engine.callbacks.onFinish = (result) => {
      this.result = result;
      this.stopRecording();
      this.setResultReady(result);
    };

    engine.callbacks.onPhaseChange = (phase: GamePhase) => {
      if (phase === 'running') {
        this.el.querySelector('.game-topbar')?.classList.add('running');
        this.startRecording();
        this.startLiveRankTimer();
      }
      if (phase === 'finished') {
        this.stopLiveRankTimer();
        this.updateRankList(true);
      }
    };

    return engine;
  }

  private bindOnlineResultSync() {
    if (!this.config.isOnline || !this.config.onlineRoom || this.config.isOnlineHost) return;
    this.unsubscribeOnline = this.config.onlineRoom.onEvent((event: unknown) => {
      if (
        typeof event === 'object' &&
        event !== null &&
        (event as { type?: unknown }).type === 'game_result'
      ) {
        const payload = (event as { payload?: unknown }).payload;
        if (this.isGameResult(payload)) {
          this.applyOnlineResult(payload);
        }
      }
    });
  }

  private isGameResult(value: unknown): value is GameResult {
    return (
      typeof value === 'object' &&
      value !== null &&
      Array.isArray((value as GameResult).rankings) &&
      typeof (value as GameResult).timestamp === 'number'
    );
  }

  private applyOnlineResult(result: GameResult) {
    if (this.onlineResultApplied) return;
    this.onlineResultApplied = true;
    this.result = result;
    this.stopRecording();
    this.engine?.destroy();
    this.engine = null;
    this.rankItems = result.rankings.map((r) => ({ rank: r.rank, name: r.name, color: r.color }));
    this.updateRankList(true);
    this.showResult(result);
  }

  private updateRankList(finishedOnly = false) {
    const medals = ['🥇', '🥈', '🥉'];
    const n = this.config.students.length;
    const items = finishedOnly || !this.engine
      ? this.rankItems.map((r) => ({ ...r, finished: true }))
      : this.engine.getLiveOrder(n);

    if (this.rankList.children.length !== items.length) {
      this.rankList.innerHTML = items.map((_, i) => `
        <div class="rank-item" id="rank-item-${i}">
          <span class="rank-medal"></span>
          <span class="rank-name"></span>
          <span class="rank-state"></span>
        </div>
      `).join('');
    }

    items.forEach((item, i) => {
      const el = this.rankList.children[i] as HTMLElement;
      el.className = `rank-item ${item.finished ? 'finished' : 'live'} ${i < 3 ? 'top-3' : ''}`;
      el.style.setProperty('--rank-color', item.color);
      el.querySelector('.rank-medal')!.textContent = medals[i] ?? (i + 1) + '위';
      el.querySelector('.rank-name')!.textContent = item.name;
      el.querySelector('.rank-state')!.textContent = item.finished ? '도착' : '진행';
    });
  }

  private startLiveRankTimer() {
    if (this.liveRankTimer) return;
    this.liveRankTimer = setInterval(() => {
      if (this.engine) this.updateRankList(false);
    }, 250);
  }

  private stopLiveRankTimer() {
    if (this.liveRankTimer) {
      clearInterval(this.liveRankTimer);
      this.liveRankTimer = null;
    }
  }

  private markStudentFinished(name: string) {
    this.el.querySelectorAll('.student-badge').forEach((b) => {
      if (b.textContent?.trim() === name) {
        b.classList.add('finished');
      }
    });
  }

  private showResult(result: GameResult) {
    if (this.modal) return;
    const className = this.app.getActiveProfile()?.name ?? '학급';
    this.modal = new ResultModal(document.body, result, className, {
      onPlayAgain: () => {
        this.modal?.destroy();
        this.modal = null;
        this.restart();
      },
      onNewGame: () => {
        this.modal?.destroy();
        this.modal = null;
        this.app.navigate('home');
      },
    });
  }

  private setResultReady(result: GameResult) {
    this.result = result;
    this.resultReady = true;
    this.el.querySelector('#btn-show-result')?.classList.remove('disabled');

    if (this.config.isOnline && this.config.isOnlineHost && !this.onlineResultBroadcasted) {
      this.onlineResultBroadcasted = true;
      void this.config.onlineRoom?.broadcastResult(result);
    }
  }

  private restart() {
    this.engine?.destroy();
    this.engine = null;
    this.rankItems = [];
    this.result = null;
    this.resultReady = false;
    this.onlineResultBroadcasted = false;
    this.stopLiveRankTimer();
    this.clearReplay();
    this.rankList.innerHTML = '';
    this.el.querySelector('#btn-show-result')?.classList.add('disabled');
    this.el.querySelectorAll('.student-badge').forEach((b) => b.classList.remove('finished'));
    const eng = this.initEngine();
    eng.startCountdown();
  }

  private bindEvents() {
    this.el.querySelector('#btn-game-back')?.addEventListener('click', () => {
      this.app.navigate('roster', { mode: this.config.mode });
    });

    this.el.querySelector('#btn-fullscreen-game')?.addEventListener('click', () => {
      const wrapper = this.el.querySelector('#canvas-wrapper')!;
      if (!document.fullscreenElement) {
        wrapper.requestFullscreen().catch(() => document.documentElement.requestFullscreen());
      } else {
        document.exitFullscreen();
      }
    });

    this.el.querySelector('#btn-replay-game')?.addEventListener('click', () => {
      this.showReplay();
    });
    this.el.querySelector('#btn-download-replay')?.addEventListener('click', () => {
      this.downloadReplay();
    });
    this.el.querySelector('#btn-show-result')?.addEventListener('click', () => {
      if (!this.resultReady || !this.result) return;
      this.showResult(this.result);
    });

    this.el.querySelector('#recording-pill')?.addEventListener('click', () => {
      if (this.mediaRecorder?.state === 'recording') this.stopRecording();
    });

    this.el.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('#canvas-wrapper')) {
        this.handleGameClick();
      }
    });
    this.canvas.addEventListener('mousemove', this.handleCanvasMouseMove);
    this.canvas.addEventListener('mouseleave', this.handleCanvasMouseLeave);

    document.addEventListener('keydown', this.handleKey);
  }

  private handleGameClick() {
    resumeAudioCtx();
    if (this.guideOverlay) return;
    if (!this.engine) return;
    const phase = (this.engine as any).phase as GamePhase;
    if (phase === 'idle') this.engine.startCountdown();
  }

  private showGuide() {
    if (this.config.isOnline && !this.config.isOnlineHost) return;
    this.guideOverlay?.remove();
    this.guideOverlay = document.createElement('div');
    this.guideOverlay.className = 'game-guide-overlay';
    this.guideOverlay.innerHTML = `
      <div class="game-guide-modal">
        <div class="modal-header">
          <h3>코스 요소 안내</h3>
          <button class="btn-icon-sm" id="btn-close-guide">✕</button>
        </div>
        <div class="guide-body">
          ${this.renderGuideContent()}
        </div>
        <div class="guide-actions">
          <button class="btn-primary" id="btn-guide-start">시작하기</button>
        </div>
      </div>
    `;
    document.body.appendChild(this.guideOverlay);
    const start = () => {
      resumeAudioCtx();
      this.guideOverlay?.remove();
      this.guideOverlay = null;
      this.engine?.startCountdown();
    };
    this.guideOverlay.querySelector('#btn-guide-start')?.addEventListener('click', start);
    this.guideOverlay.querySelector('#btn-close-guide')?.addEventListener('click', start);
  }

  private renderGuideContent() {
    const courseElements = `
      <div class="guide-section-label">코스 요소</div>
      <div class="guide-row-2">
        <div class="guide-card">
          <span class="guide-icon guide-icon-popper">!</span>
          <strong>팝퍼</strong>
          <small>닿으면 공을 튕겨냄<br>한 번 작동 후 소멸</small>
        </div>
        <div class="guide-card">
          <span class="guide-icon guide-icon-bubble">○</span>
          <strong>버블</strong>
          <small>공이 닿으면 위로 밀어올림<br>잠시 후 되살아남</small>
        </div>
      </div>
    `;

    if (!this.config.itemsEnabled) {
      return courseElements;
    }

    const items = [
      ['boost',    '⚡', '가속',     '아래로 빠르게 가속'],
      ['slow',     '🧊', '감속',     '속도를 크게 감속'],
      ['chaos',    '🌀', '혼란',     '경로를 흔들어 이탈'],
      ['shield',   '🛡️', '방패',     '방해 1회 무효'],
      ['swap',     '🔀', '선두추격', '선두 근처로 이동'],
      ['teleport', '🌠', '순간이동', '앞으로 크게 점프'],
      ['vortex',   '◎',  '소용돌이', '자신 가속 + 주변 감속'],
      ['anchor',   '⬇',  '닻',       '거의 완전 정지'],
    ];

    return `
      <div class="guide-section-label">아이템 <span class="guide-label-sub">닿으면 한 번만 발동</span></div>
      <div class="guide-row-4">
        ${items.map(([kind, icon, name, desc]) => `
          <div class="guide-card">
            <span class="guide-icon guide-icon-item-${kind}">${icon}</span>
            <strong>${name}</strong>
            <small>${desc}</small>
          </div>
        `).join('')}
      </div>
      ${courseElements}
    `;
  }

  private handleKey = (e: KeyboardEvent) => {
    if (!this.engine) return;
    if (e.key === ' ') {
      e.preventDefault();
      this.handleGameClick();
    }
    if (e.key === 'r' || e.key === 'R') {
      this.restart();
    }
  };

  private handleCanvasMouseMove = (e: MouseEvent) => {
    this.engine?.handlePointerMove(e.clientX, e.clientY);
  };

  private handleCanvasMouseLeave = () => {
    this.engine?.clearPointerHover();
  };

  private startRecording() {
    if (this.mediaRecorder || typeof MediaRecorder === 'undefined') return;
    const stream = this.canvas.captureStream(60);
    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : 'video/webm';
    this.recordingChunks = [];
    this.mediaRecorder = new MediaRecorder(stream, {
      mimeType: mime,
      videoBitsPerSecond: 6_000_000,
    });
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.recordingChunks.push(event.data);
    };
    this.mediaRecorder.onstop = () => {
      const blob = new Blob(this.recordingChunks, { type: 'video/webm' });
      if (this.replayUrl) URL.revokeObjectURL(this.replayUrl);
      this.replayUrl = URL.createObjectURL(blob);
      this.mediaRecorder = null;
      this.el.querySelector('#recording-pill')?.classList.remove('active');
      this.el.querySelector('#btn-replay-game')?.classList.remove('disabled');
      this.el.querySelector('#btn-download-replay')?.classList.remove('disabled');
    };
    this.mediaRecorder.start(250);
    this.el.querySelector('#recording-pill')?.classList.add('active');
  }

  private stopRecording() {
    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.stop();
    }
  }

  private clearReplay() {
    this.stopRecording();
    if (this.replayUrl) {
      URL.revokeObjectURL(this.replayUrl);
      this.replayUrl = null;
    }
    this.el.querySelector('#btn-replay-game')?.classList.add('disabled');
    this.el.querySelector('#btn-download-replay')?.classList.add('disabled');
    this.el.querySelector('#recording-pill')?.classList.remove('active');
  }

  private showReplay() {
    if (!this.replayUrl) return;
    this.replayOverlay?.remove();
    this.replayOverlay = document.createElement('div');
    this.replayOverlay.className = 'replay-overlay';
    this.replayOverlay.innerHTML = `
      <div class="replay-modal">
        <div class="replay-header">
          <h3>당첨 장면 리플레이</h3>
          <div class="replay-actions">
            <button class="btn-sm" id="btn-download-replay-modal">다운로드</button>
            <button class="btn-icon-sm" id="btn-close-replay">✕</button>
          </div>
        </div>
        <video src="${this.replayUrl}" controls autoplay playsinline></video>
      </div>
    `;
    document.body.appendChild(this.replayOverlay);
    this.replayOverlay.querySelector('#btn-close-replay')?.addEventListener('click', () => {
      this.replayOverlay?.remove();
      this.replayOverlay = null;
    });
    this.replayOverlay.querySelector('#btn-download-replay-modal')?.addEventListener('click', () => {
      this.downloadReplay();
    });
  }

  private downloadReplay() {
    if (!this.replayUrl) return;
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = this.replayUrl;
    a.download = `굴려굴려뽑기_리플레이_${stamp}.webm`;
    a.click();
  }

  destroy() {
    document.removeEventListener('keydown', this.handleKey);
    this.canvas?.removeEventListener('mousemove', this.handleCanvasMouseMove);
    this.canvas?.removeEventListener('mouseleave', this.handleCanvasMouseLeave);
    this.unsubscribeOnline?.();
    this.stopLiveRankTimer();
    this.clearReplay();
    this.replayOverlay?.remove();
    this.guideOverlay?.remove();
    void this.config.onlineRoom?.leave?.();
    this.engine?.destroy();
    this.modal?.destroy();
    this.el.remove();
  }
}
